import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Snackbar,
    Alert,
    Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';
import Cookies from '../../../common/Cookies';

const badgePrepends = [
    'Response Quality',
    'Speed & Latency',
    'Context Window',
    'Creativity & Tone',
    'Reliability & Stability'
];

const ModelSelectionSection = () => {
    const [models, setModels] = useState([]);
    const [selectedModelId, setSelectedModelId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [pendingModelId, setPendingModelId] = useState(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Get tenant ID from cookies
    const tenantId = Cookies.getTenantId();

    // Fetch available models and current selection
    useEffect(() => {
        fetchModels();
    }, []);

    const fetchModels = async (retryCount = 0) => {
        try {
            setLoading(true);
            const response = await axios.get(`/account/models?tenantId=${tenantId}`);

            if (response.data.success) {
                // Filter out Titan and Jamba models
                setModels(response.data.availableModels.filter(model => model.modelId !== 'amazon.titan-text-express-v1' && model.modelId !== 'ai21.jamba-instruct-v1:0'));
                setSelectedModelId(
                    response.data.selectedModelId === 'amazon.titan-text-express-v1' || response.data.selectedModelId === 'ai21.jamba-instruct-v1:0'
                        ? null
                        : response.data.selectedModelId
                );
            } else {
                showSnackbar('Failed to load models', 'error');
            }
        } catch (error) {
            console.error('Error fetching models:', error);

            // Retry logic for network errors
            if (retryCount < 2 && error.code === 'NETWORK_ERROR') {
                setTimeout(() => fetchModels(retryCount + 1), 1000);
                return;
            }

            showSnackbar('Error loading models', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleModelSelect = (modelId) => {
        if (modelId === selectedModelId) return; // Already selected

        setPendingModelId(modelId);
        setConfirmDialogOpen(true);
    };

    const handleConfirmChange = async () => {
        try {
            setUpdating(true);
            setConfirmDialogOpen(false);

            const response = await axios.post(`/account/update-model?tenantId=${tenantId}&modelId=${pendingModelId}`);

            if (response.data.success) {
                setSelectedModelId(pendingModelId);
                showSnackbar(`Model updated to ${response.data.selectedModelName}`, 'success');
            } else {
                showSnackbar(response.data.message || 'Failed to update model', 'error');
            }
        } catch (error) {
            console.error('Error updating model:', error);
            showSnackbar('Error updating model', 'error');
        } finally {
            setUpdating(false);
            setPendingModelId(null);
        }
    };

    const handleCancelChange = () => {
        setConfirmDialogOpen(false);
        setPendingModelId(null);
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const closeSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const getModelDescription = (modelId, provider) => {
        switch (modelId) {
            case 'anthropic.claude-3-haiku-20240307-v1:0':
                return 'Ultra-quick model built for short, factual interactions and simple automations.';
            case 'gpt-4o':
                return 'Flagship multimodal model balancing conversational warmth with analytical reasoning.';
            case 'gpt-4o-mini':
                return 'Compact 4o variant that keeps near-flagship quality while responding faster.';
            case 'gpt-4.1':
                return 'Next-generation 4-series model with major improvements in reasoning and long-context awareness.';
            case 'gpt-4.1-mini':
                return 'Balanced version offering GPT-4.1 understanding at higher speed and responsiveness.';
            case 'gpt-4.1-nano':
                return 'Optimized for ultra-fast, high-volume conversations where brevity matters most.';
            default:
                return 'High-quality language model for natural conversations and assistance.';
        }
    };

    const getModelBadges = (modelId) => {
        switch (modelId) {
            case 'anthropic.claude-3-haiku-20240307-v1:0':
                return [
                    '🟡 Accurate on simple queries, less nuanced on complex ones',
                    '🟢 Lightning fast for real-time chat',
                    '🔵 Moderate',
                    '🟡 Clear but less expressive',
                    '🟢 Extremely consistent responses'
                ];
            case 'gpt-4o':
                return [
                    '🟢 Excellent fluency and factual grounding',
                    '🟡 Moderate startup, smooth streaming',
                    '🟢 Large',
                    '🟢 Great tone control and personalization',
                    '🟢 Very strong across complex dialogs'
                ];
            case 'gpt-4o-mini':
                return [
                    '🟡 High quality with slight depth trade-offs',
                    '🟢 Snappy, ideal for live support',
                    '🟢 Large',
                    '🟡 Natural but less stylistic range',
                    '🟢 Steady and predictable output'
                ];
            case 'gpt-4.1':
                return [
                    '🟢 Advanced reasoning and precision',
                    '🟡 Slightly slower initial response',
                    '🟢 Very Large',
                    '🟢 Engaging, context-sensitive phrasing',
                    '🟢 Robust for sustained sessions'
                ];
            case 'gpt-4.1-mini':
                return [
                    '🟡 Strong for typical conversations',
                    '🟢 Quick responses for chat workflows',
                    '🟢 Large',
                    '🟢 Friendly and adaptive',
                    '🟢 Consistent over long sessions'
                ];
            case 'gpt-4.1-nano':
                return [
                    '🔵 Good for short, direct answers',
                    '🟢 Instantaneous feel',
                    '🟡 Medium',
                    '🟡 Straightforward, limited variation',
                    '🟢 Dependable for repetitive chat'
                ];
            default:
                return ['✨ Quality'];
        }
    };

    const getDisplayName = (model) => {
        switch (model.modelId) {
            case 'anthropic.claude-3-haiku-20240307-v1:0':
                return 'Claude 3 Haiku (Anthropic)';
            case 'gpt-4o':
                return 'GPT-4o (OpenAI)';
            case 'gpt-4o-mini':
                return 'GPT-4o Mini (OpenAI)';
            case 'gpt-4.1':
                return 'GPT-4.1 (OpenAI)';
            case 'gpt-4.1-mini':
                return 'GPT-4.1 Mini (OpenAI)';
            case 'gpt-4.1-nano':
                return 'GPT-4.1 Nano (OpenAI)';
            default:
                return model.name;
        }
    };

    if (loading) {
        return (
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                    Choose Your Model
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                    <CircularProgress />
                </Box>
            </Paper>
        );
    }

    return (
        <>
            <Paper elevation={0} sx={{ p: 3 }}>
                <Box sx={{ mb: 3, textAlign: 'left' }}>
                    <Typography variant="h6" gutterBottom sx={{ textAlign: 'left' }}>
                        Choose Your Model
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                        Select the AI model that best fits your needs. Each model has different strengths and capabilities.
                    </Typography>
                </Box>

                <Grid container spacing={3}>
                    {models.map((model, idx) => {
                        const isSelected = model.id === selectedModelId;
                        const isPending = updating && model.id === pendingModelId;

                        return (
                            <Grid item xs={12} sm={6} md={4} key={model.id}>
                                <Card
                                    sx={{
                                        cursor: 'pointer',
                                        height: 350, // Increased from 220 to 270px (50px more)
                                        minHeight: 270,
                                        maxWidth: 500, // Maximum width for better alignment
                                        width: '100%',
                                        border: isSelected ? 2 : 1,
                                        borderColor: isSelected ? 'primary.main' : 'divider',
                                        backgroundColor: isSelected ? 'primary.50' : 'background.paper',
                                        transition: 'all 0.3s ease',
                                        opacity: updating && !isPending ? 0.6 : 1,
                                        position: 'relative',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        '&:hover': {
                                            transform: updating ? 'none' : 'translateY(-4px)',
                                            boxShadow: updating ? 1 : 4,
                                        }
                                    }}
                                    onClick={() => !updating && handleModelSelect(model.id)}
                                >
                                    <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                        {/* Header with model name and selection indicator */}
                                        <Box sx={{ display: 'center', alignItems: 'flex-start', mb: 2, width: '100%' }}>
                                            <Box sx={{ width: '100%' }}>
                                                <Typography
                                                    variant="h6"
                                                    component="div"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        textAlign: 'center', // Center the model name
                                                        width: '100%',
                                                    }}
                                                >
                                                    {getDisplayName(model)}
                                                </Typography>
                                            </Box>
                                            {isSelected && (
                                                <CheckCircleIcon color="primary" sx={{ fontSize: 28, position: 'absolute', right: 24, top: 24 }} />
                                            )}
                                        </Box>

                                        {/* Model description */}
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ flexGrow: 1, mb: 2 }}
                                        >
                                            {getModelDescription(model.modelId, model.provider)}
                                        </Typography>

                                        {/* Model badges */}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2, width: '100%' }}>
                                            {getModelBadges(model.modelId).slice(0, 5).map((badge, index) => (
                                                <Box
                                                    key={index}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        width: '100%',
                                                        justifyContent: 'flex-start',
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            mr: 1, // Small margin to the right of the text
                                                            textAlign: 'left',
                                                            flexShrink: 0,
                                                            whiteSpace: 'nowrap', // Prevents label from wrapping
                                                        }}
                                                    >
                                                        {badgePrepends[index]}
                                                    </Typography>
                                                    <Chip
                                                        label={badge}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ fontSize: '0.7rem', height: 20 }}
                                                    />
                                                </Box>
                                            ))}
                                        </Box>

                                        {/* Status indicators */}
                                        <Box sx={{ mt: 'auto' }}>
                                            {isPending && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2 }}>
                                                    <CircularProgress size={20} sx={{ mr: 1 }} />
                                                    <Typography variant="body2" color="primary">
                                                        Changing Models, Please Wait...
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>

                {/* Legend under the first card, left aligned */}
                <Box sx={{ mt: 3, ml: 0, textAlign: 'left', width: '100%' }}>

                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                        🟢 = strength / best fit&nbsp;&nbsp;🟡 = solid / balanced&nbsp;&nbsp;🔵 = limited / narrower&nbsp;&nbsp;
                    </Typography>
                </Box>
            </Paper>

            {/* Confirmation Dialog */}
            <Dialog open={confirmDialogOpen} onClose={handleCancelChange} maxWidth="sm" fullWidth>
                <DialogTitle>Confirm Model Change</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to change models? This will affect how your AI responds to future conversations.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelChange} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmChange} color="primary" variant="contained">
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success/Error Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={closeSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={closeSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default ModelSelectionSection;
